import React, { useState } from "react";
import { FaRegSave, FaTrash, FaPencilAlt } from "react-icons/fa";
import "./AddEditTask.css";

const TIMING_OPTIONS = ["Not timed - overrides lab setting", "Timed - overrides lab setting", "Use lab setting"];
const UNIT_OPTIONS = ["Inherit Procedure Unit", "Per Unit", "Per Case"];

const COMPLETION = [
  { key: "outOfOrderScan", label: "Task completion page, allow Out-of-order scanning" },
  { key: "outOfOrderCheck", label: 'Allow Out-of-order checking, on "Case details" task chart.' },
  { key: "sharedSession", label: "Complete all instances of this task in a case, also open a shared work-session to be timed." },
  { key: "batched", label: "Allow Batched Completion scanning." },
];

const Row = ({ label, required, children, top }) => (
  <div className={"tf-row" + (top ? " tf-top" : "")}>
    <label className="tf-label">{label}{required && <span className="tf-req"> *</span>}</label>
    <div className="tf-ctl">{children}</div>
  </div>
);

const Section = ({ title, children }) => (
  <>
    <h4 className="tf-section">{title}</h4>
    <div className="tf-panel">{children}</div>
  </>
);

const Entries = ({ n }) => (
  <div className="tf-entries">
    <span>Showing {n ? 1 : 0} to {n} of {n} entries</span>
    <span className="tf-arrows"><i>‹</i><i>›</i></span>
  </div>
);

// Sample option lists - replace with API data or pass users / procedures / attributes as props
const DEFAULT_USERS = ["User 1", "User 2"];
const DEFAULT_PROCEDURES = ["Procedure 1", "Procedure 2"];
const DEFAULT_ATTRIBUTES = ["Attribute 1", "Attribute 2"];

// cell text for tables whose rows are edited in a modal
const turnCell = (r, k) =>
  k === "sameDay" ? (r.sameDay ? "Yes" : "") : k === "decisionPoint" ? [r.procedure, r.attribute, r.value].filter(Boolean).join(" / ") : r[k];
const payCell = (r, k) => (k === "name" ? [r.user, r.procedure].filter(Boolean).join(" - ") : r.amount);

// Popup used for "Add / Edit Turn Around" and "Add / Edit User Payment"
const Modal = ({ title, heading, okLabel, error, width, onClose, onOk, children }) => (
  <div className="tf-overlay">
    <div className="tf-modal" style={{ width }}>
      <div className="tf-mhead">
        <span>{title}</span>
        <button type="button" onClick={onClose} aria-label="Close">×</button>
      </div>
      <div className="tf-mbody">
        <h4 className="tf-mtitle">{heading}</h4>
        <div className="tf-mpanel">
          {children}
          {error && <div className="tf-error">{error}</div>}
        </div>
      </div>
      <div className="tf-mfoot">
        <button type="button" className="tf-btn tf-mbtn" onClick={onClose}>Cancel</button>
        <button type="button" className="tf-btn tf-mbtn" onClick={onOk}>{okLabel}</button>
      </div>
    </div>
  </div>
);

// Small table. onEdit given -> read-only rows with edit + delete buttons (modal editing)
// onEdit not given -> inline input row(s) with delete button (Process Improvement Issues)
const MiniTable = ({ columns, rows, onAdd, onEdit, onChange, onRemove, cell }) => (
  <div className="tf-mini">
    <div className="tf-add-row">
      <button type="button" className="tf-btn tf-add" onClick={onAdd}>+ Add</button>
    </div>
    <Entries n={rows.length} />
    <table className="tf-table">
      <thead>
        <tr>
          {columns.map((c) => <th key={c.key}>{c.label}</th>)}
          {rows.length > 0 && <th className="tf-x" />}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr><td colSpan={columns.length} className="tf-empty">No data available in table</td></tr>
        ) : (
          rows.map((r, i) => (
            <tr key={i}>
              {columns.map((c) => (
                <td key={c.key}>
                  {onEdit ? (
                    cell(r, c.key)
                  ) : (
                    <input className="tf-input tf-cell tf-wide" value={r[c.key]} onChange={(e) => onChange(i, c.key, e.target.value)} />
                  )}
                </td>
              ))}
              <td className="tf-x">
                {onEdit && <button type="button" className="tf-ibtn" onClick={() => onEdit(i)} title="Edit"><FaPencilAlt /></button>}
                <button type="button" className="tf-ibtn" onClick={() => onRemove(i)} title="Delete"><FaTrash /></button>
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
    <Entries n={rows.length} />
  </div>
);

// Add Task page. onSave(taskFields, fullFormData), onCancel()
const AddTask = ({ departments = [], users = DEFAULT_USERS, procedures = DEFAULT_PROCEDURES, attributes = DEFAULT_ATTRIBUTES, onSave, onCancel }) => {
  const [f, setF] = useState({
    name: "",
    department: "",
    progressStages: "",
    payAmount: 0,
    timing: TIMING_OPTIONS[0],
    completion: { outOfOrderScan: false, outOfOrderCheck: true, sharedSession: false, batched: false },
    customId: "",
    dailyLimit: 0,
    countCapacity: true,
    units: UNIT_OPTIONS[0],
    turnAround: [],
    assignable: false,
    payments: [],
    issues: [],
  });
  const [error, setError] = useState("");

  const upd = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const rows = (k, blank) => ({
    rows: f[k],
    onAdd: () => upd(k, [...f[k], { ...blank }]),
    onChange: (i, c, v) => upd(k, f[k].map((r, j) => (j === i ? { ...r, [c]: v } : r))),
    onRemove: (i) => upd(k, f[k].filter((_, j) => j !== i)),
  });

  // ---- modal (Turn Around / User Payment): add + edit ----
  const [modal, setModal] = useState(null); // { type: "turn" | "pay", index: number | null, data, error }
  const BLANK = {
    turn: { days: "", sameDay: false, procedure: "", attribute: "", value: "" },
    pay: { user: "", procedure: "", amount: 0 },
  };
  const LISTS = { turn: "turnAround", pay: "payments" };
  const openModal = (type, index = null) =>
    setModal({ type, index, data: index === null ? { ...BLANK[type] } : { ...f[LISTS[type]][index] }, error: "" });
  const setData = (k, v) => setModal((m) => ({ ...m, data: { ...m.data, [k]: v }, error: "" }));
  const saveModal = () => {
    const { type, index, data } = modal;
    if (type === "turn" && String(data.days).trim() === "") return setModal({ ...modal, error: "Turn Around Days is required" });
    if (type === "pay" && (!data.user || !data.procedure || String(data.amount).trim() === ""))
      return setModal({ ...modal, error: "User, Procedure and Amount are required" });
    const key = LISTS[type];
    upd(key, index === null ? [...f[key], data] : f[key].map((r, j) => (j === index ? data : r)));
    setModal(null);
  };
  const removeAt = (key) => (i) => upd(key, f[key].filter((_, j) => j !== i));

  const submit = () => {
    if (!f.name.trim()) return setError("Name is required");
    setError("");
    onSave(
      {
        name: f.name.trim(),
        department: f.department,
        dailyLimit: f.dailyLimit === 0 || f.dailyLimit === "0" ? "" : f.dailyLimit,
        turnAroundDays: "",
      },
      f
    );
  };

  return (
    <div className="tf-page">
      <h1 className="tf-title">Add Task</h1>

      <Section title="Task Details">
        <Row label="Name" required>
          <input className="tf-input" placeholder="Enter task name" value={f.name} onChange={(e) => upd("name", e.target.value)} />
          {error && <div className="tf-error">{error}</div>}
        </Row>
        <Row label="Department">
          <select className="tf-input" value={f.department} onChange={(e) => upd("department", e.target.value)}>
            <option value=""></option>
            {departments.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </Row>
        <Row label="Progress Stages">
          <input className="tf-input tf-tall" value={f.progressStages} onChange={(e) => upd("progressStages", e.target.value)} />
        </Row>
        <Row label="Pay Amount (£)">
          <input className="tf-input tf-short" type="number" value={f.payAmount} onChange={(e) => upd("payAmount", e.target.value)} />
        </Row>
        <Row label="Task Timing" top>
          <div className="tf-hint">When enabled, timed tasks must be scanned twice, 'Open a work-session' and 'Close work-session and complete task' events.</div>
          <select className="tf-input" value={f.timing} onChange={(e) => upd("timing", e.target.value)}>
            {TIMING_OPTIONS.map((o) => <option key={o}>{o}</option>)}
          </select>
        </Row>
        <Row label="Task Completion" top>
          {COMPLETION.map((c) => (
            <label className="tf-check" key={c.key}>
              <input type="checkbox" checked={f.completion[c.key]} onChange={(e) => upd("completion", { ...f.completion, [c.key]: e.target.checked })} />
              <span>{c.label}</span>
            </label>
          ))}
        </Row>
        <Row label="Custom ID">
          <input className="tf-input" placeholder="Custom Identifier" value={f.customId} onChange={(e) => upd("customId", e.target.value)} />
        </Row>
      </Section>

      <Section title="Task Scheduling">
        <Row label="Daily Limit">
          <div className="tf-inline">
            <input className="tf-input tf-short" type="number" value={f.dailyLimit} onChange={(e) => upd("dailyLimit", e.target.value)} />
            <span>Task Blocks</span>
          </div>
        </Row>
        <Row label="">
          <label className="tf-check">
            <input type="checkbox" checked={f.countCapacity} onChange={(e) => upd("countCapacity", e.target.checked)} />
            <span>Count toward department capacity</span>
          </label>
        </Row>
        <Row label="Task Units">
          <select className="tf-input tf-mid" value={f.units} onChange={(e) => upd("units", e.target.value)}>
            {UNIT_OPTIONS.map((o) => <option key={o}>{o}</option>)}
          </select>
        </Row>
        <Row label="Turn Around Days" />
        <MiniTable
          columns={[
            { key: "days", label: "Days" },
            { key: "sameDay", label: "Same Day" },
            { key: "decisionPoint", label: "Decision Point" },
          ]}
          rows={f.turnAround}
          cell={turnCell}
          onAdd={() => openModal("turn")}
          onEdit={(i) => openModal("turn", i)}
          onRemove={removeAt("turnAround")}
        />
      </Section>

      <Section title="Task-User Scheduling">
        <label className="tf-check tf-right">
          <input type="checkbox" checked={f.assignable} onChange={(e) => upd("assignable", e.target.checked)} />
          <span>This is an assignable task</span>
        </label>
      </Section>

      <Section title="User Payment Details">
        <MiniTable
          columns={[{ key: "name", label: "Name" }, { key: "amount", label: "Amount" }]}
          rows={f.payments}
          cell={payCell}
          onAdd={() => openModal("pay")}
          onEdit={(i) => openModal("pay", i)}
          onRemove={removeAt("payments")}
        />
      </Section>

      <Section title="Process Improvement Issues">
        <MiniTable columns={[{ key: "name", label: "Name" }]} {...rows("issues", { name: "" })} />
      </Section>

      <div className="tf-actions">
        <button type="button" className="tf-btn tf-update" onClick={submit}><FaRegSave /> Save</button>
        <button type="button" className="tf-btn tf-cancel" onClick={onCancel}>Cancel</button>
      </div>

      {modal?.type === "turn" && (
        <Modal
          title={modal.index === null ? "Add Turn Around" : "Edit Turn Around"}
          heading="Details"
          okLabel="Save"
          width={620}
          error={modal.error}
          onClose={() => setModal(null)}
          onOk={saveModal}
        >
          <Row label="Turn Around Days" required>
            <input className="tf-input" type="number" value={modal.data.days} onChange={(e) => setData("days", e.target.value)} />
          </Row>
          <Row label="">
            <label className="tf-check">
              <input type="checkbox" checked={modal.data.sameDay} onChange={(e) => setData("sameDay", e.target.checked)} />
              <span>Same Day as Previous 1+ Day Task</span>
            </label>
          </Row>
          <Row label="Procedure">
            <select className="tf-input" value={modal.data.procedure} onChange={(e) => setData("procedure", e.target.value)}>
              <option value="" />
              {procedures.map((p) => <option key={p}>{p}</option>)}
            </select>
          </Row>
          <Row label="Attribute">
            <select className="tf-input" value={modal.data.attribute} onChange={(e) => setData("attribute", e.target.value)}>
              <option value="" />
              {attributes.map((a) => <option key={a}>{a}</option>)}
            </select>
          </Row>
          <Row label="Value">
            <input className="tf-input" value={modal.data.value} onChange={(e) => setData("value", e.target.value)} />
          </Row>
        </Modal>
      )}

      {modal?.type === "pay" && (
        <Modal
          title={modal.index === null ? "Add User Payment" : "Edit User Payment"}
          heading="User Payment Details"
          okLabel={modal.index === null ? "Add" : "Save"}
          width={500}
          error={modal.error}
          onClose={() => setModal(null)}
          onOk={saveModal}
        >
          <Row label="User" required>
            <select className="tf-input" value={modal.data.user} onChange={(e) => setData("user", e.target.value)}>
              <option value="">Select User</option>
              {users.map((u) => <option key={u}>{u}</option>)}
            </select>
          </Row>
          <Row label="Procedure" required>
            <select className="tf-input" value={modal.data.procedure} onChange={(e) => setData("procedure", e.target.value)}>
              <option value="">Select Procedure</option>
              {procedures.map((p) => <option key={p}>{p}</option>)}
            </select>
          </Row>
          <Row label="Amount (£)" required>
            <input className="tf-input" type="number" value={modal.data.amount} onChange={(e) => setData("amount", e.target.value)} />
          </Row>
        </Modal>
      )}
    </div>
  );
};

export default AddTask;